import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PullToRefresh from 'react-simple-pull-to-refresh';
import Spinner from '@chronos/components/Spinner';
import * as utils from '@chronos/utils';
import {
  setHeaderTitle,
  fetchListsWithCards,
  selectCardsStats,
  selectCardsEstimates,
  fetchCardsStats,
  selectLiveWorklogs,
  selectListsWithUserCards,
} from '@chronos/slices/boardSlice';
import eventEmitter from '@chronos/utils/eventEmitter';
import { useBoardContext } from '@chronos/ui-toolkit/contexts/index';
import {
  initialUserState,
  useGetUsersQuery,
  useGetAuthUserQuery,
} from '@chronos/slices/usersApi';
import { getState, saveState } from '../../utils/state-saver';
import { STATE_WORKLOGS_KEY } from '../../constants';
import { setLabelExpanded, selectIsLabelExpanded } from '../../slices/appSlice';
import CardsList from './CardsList';
import * as S from './styled';

const Cards = ({ isMyOnly }) => {
  const dispatch = useDispatch();
  const { boardId, organizationId, board } = useBoardContext();

  const cardsStats = useSelector(selectCardsStats);
  const cardsEstimates = useSelector(selectCardsEstimates);
  const liveWorklogs = useSelector(selectLiveWorklogs);
  const { data: { entities: usersEntities } = initialUserState } =
    useGetUsersQuery({
      boardId,
      organizationId,
    });
  const { data: profile } = useGetAuthUserQuery(null);
  const lists = useSelector(
    (state) => selectListsWithUserCards(state, { profile, isMyOnly }),
    shallowEqual
  );
  const isLabelExpanded = useSelector(selectIsLabelExpanded);

  const [activeKeys, setActiveKeys] = useState([]);

  const setActiveKeysCallback = useCallback(
    (keys) => {
      setActiveKeys(keys);
      saveState(STATE_WORKLOGS_KEY, { activeKeys: keys });
    },
    [setActiveKeys]
  );

  const onUpdateState = useCallback(() => {
    const cardIds = lists.reduce((acc, list) => {
      return [...acc, ...list.cards.map((item) => item.id)];
    }, []);
    dispatch(fetchCardsStats({ boardId, cardIds }));
  }, [boardId, isMyOnly, lists, fetchCardsStats]);

  useEffect(() => {
    if (board) {
      dispatch(setHeaderTitle(board.name));
    }
  }, [board]);

  useEffect(() => {
    dispatch(fetchListsWithCards({ boardId, fetchStats: true }));
  }, [boardId]);

  useEffect(() => {
    eventEmitter.on('saveWorklogFinish', onUpdateState);
    eventEmitter.on('updateState', onUpdateState);

    return () => {
      eventEmitter.off('updateState', onUpdateState);
      eventEmitter.off('saveWorklogFinish', onUpdateState);
    };
  }, [onUpdateState]);

  useEffect(() => {
    let firstNotEmptyListId = null;
    lists.forEach((list) => {
      if (!firstNotEmptyListId && list.cards?.length > 0) {
        firstNotEmptyListId = list.id;
      }
    });
    const stateKeys = getState(STATE_WORKLOGS_KEY, 'activeKeys');
    setActiveKeysCallback(
      stateKeys?.length > 0 ? stateKeys : firstNotEmptyListId
    );
  }, [isMyOnly, lists.length]);

  const getWhoisTracking = (cardId) => {
    const cardLiveWorklogs = liveWorklogs.filter(
      (item) => item.cardId === cardId
    );
    return utils.getWhoisTracking({
      users: usersEntities,
      trackingIds: cardLiveWorklogs.map((item) => item.memberId),
    });
  };

  const onClickExpandLabel = (event) => {
    event.stopPropagation();
    dispatch(setLabelExpanded(!isLabelExpanded));
  };

  return (
    <PullToRefresh
      refreshingContent={<Spinner />}
      pullingContent={null}
      onRefresh={async () => {
        dispatch(fetchListsWithCards({ boardId, fetchStats: true }));
      }}
    >
      <S.Cards>
        <S.Collapse
          ghost
          activeKey={activeKeys}
          onChange={setActiveKeysCallback}
        >
          {lists.map((list) => (
            <S.Panel
              header={`${list.name} (${list.cards.length})`}
              key={list.id}
            >
              <CardsList
                cards={list.cards}
                cardsStats={cardsStats}
                cardsEstimates={cardsEstimates}
                liveWorklogs={liveWorklogs}
                isLabelExpanded={isLabelExpanded}
                onClickExpandLabel={onClickExpandLabel}
                getWhoisTracking={getWhoisTracking}
              />
            </S.Panel>
          ))}
        </S.Collapse>
      </S.Cards>
    </PullToRefresh>
  );
};

Cards.propTypes = {
  /**
   * Card assign to you
   */
  isMyOnly: PropTypes.bool,
};

Cards.defaultProps = {
  isMyOnly: false,
};

export default Cards;
